import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  YAxis,
  XAxis,
  CartesianGrid,
  Tooltip,
  Bar,
  Cell,
} from "recharts";
import { Box, Typography, useTheme, Chip } from "@mui/material";
import { useCurrencyFormatter } from "../../../../contexts/CurrencyContext";
import { BudgetStorage } from "../../../../utils/BudgetStorage";

interface MonthlyReportsActualVsPlannedProps {
  totalAmount: number;
  year: number;
  month: string;
  isExpense: boolean;
  isCompact?: boolean;
}

const MonthlyReportsActualVsPlannedComponent: React.FC<
  MonthlyReportsActualVsPlannedProps
> = ({ totalAmount, year, month, isExpense, isCompact = false }) => {
  const { formatCompactCurrencyNumber, formatCurrencyNumber } =
    useCurrencyFormatter();
  const theme = useTheme();
  
  const plannedAmount = BudgetStorage.getTotalBudget(year, month, isExpense);
  
  // Different terminology for expenses vs income
  const plannedLabel = isExpense ? "Budget" : "Target";
  const noDataLabel = isExpense ? "No budget set" : "No target set";
  const noDataDescription = isExpense 
    ? "Set a budget to track your spending performance"
    : "Set income targets to track your earning performance";
  
  const plannedVsActual = [
    { label: plannedLabel, value: plannedAmount },
    { label: "Actual", value: totalAmount },
  ];

  // Calculate variance with appropriate semantics
  const variance = totalAmount - plannedAmount;
  const isOverPlanned = totalAmount > plannedAmount;
  
  // Different color logic for expenses vs income
  let varianceColor: 'success' | 'error' = 'success';
  let varianceLabel: string = '';
  
  if (plannedAmount > 0) {
    if (isExpense) {
      // For expenses: over budget is bad (red), under budget is good (green)
      varianceColor = isOverPlanned ? 'error' : 'success';
      varianceLabel = isOverPlanned ? 'Over Budget' : 'Under Budget';
    } else {
      // For income: above target is good (green), below target is bad (red)
      varianceColor = isOverPlanned ? 'success' : 'error';
      varianceLabel = isOverPlanned ? 'Above Target' : 'Below Target';
    }
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box sx={{
          backgroundColor: 'white',
          p: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          boxShadow: theme.shadows[4],
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {label}
          </Typography>
          <Typography variant="body2" color="primary">
            {formatCurrencyNumber(payload[0].value)}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (isCompact) {
    return (
      <Box sx={{ 
        width: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        overflow: 'hidden',
        px: 2, // Increased padding
        py: 1,
      }}>
        {/* Compact Summary */}
        <Box sx={{ textAlign: 'center', mb: 2, width: '100%', maxWidth: '100%' }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              color: theme.palette.primary.main,
              fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.4rem' }, // Better responsive sizing
              wordBreak: 'break-word',
              lineHeight: 1.1,
              whiteSpace: 'normal', // Allow text wrapping
              overflowWrap: 'break-word', // Break long words if needed
            }}
          >
            {formatCurrencyNumber(totalAmount)}
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              fontSize: '0.85rem',
              mt: 0.5,
            }}
          >
            Total Amount
          </Typography>
        </Box>

        {/* Compact Status */}
        <Box sx={{ mb: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
          {plannedAmount > 0 ? (
            <Chip
              label={
                isOverPlanned
                  ? `${((totalAmount / plannedAmount - 1) * 100).toFixed(1)}% ${isExpense ? 'over' : 'above'}`
                  : `${((1 - totalAmount / plannedAmount) * 100).toFixed(1)}% ${isExpense ? 'under' : 'below'}`
              }
              color={varianceColor}
              size="small"
              sx={{ 
                fontWeight: 600,
                fontSize: '0.75rem',
                maxWidth: '90%',
                '& .MuiChip-label': {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }
              }}
            />
          ) : (
            <Chip
              label={noDataLabel}
              variant="outlined"
              size="small"
              sx={{ fontSize: '0.75rem' }}
            />
          )}
        </Box>

        {/* Compact Chart */}
        <Box sx={{ 
          width: '100%', 
          height: 130, // Slightly increased height
          minHeight: 130,
          '& .recharts-wrapper': {
            width: '100% !important',
          }
        }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={plannedVsActual}
              margin={{ top: 10, right: 15, left: 15, bottom: 10 }}
              barCategoryGap="25%"
            >
              <defs>
                <linearGradient id="plannedGradientCompact" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={theme.palette.grey[400]} stopOpacity={0.8} />
                  <stop offset="100%" stopColor={theme.palette.grey[600]} stopOpacity={0.8} />
                </linearGradient>
                <linearGradient id="actualGradientCompact" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={theme.palette.primary.light} stopOpacity={0.9} />
                  <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0.9} />
                </linearGradient>
              </defs>

              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />

              <YAxis
                tickFormatter={(val) => formatCompactCurrencyNumber(val)}
                tick={{ fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                width={45} // Increased width for longer currency values
              />

              <Tooltip content={<CustomTooltip />} />

              <Bar
                dataKey="value"
                radius={[4, 4, 0, 0]}
                maxBarSize={60}
              >
                {plannedVsActual.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={index === 0 ? "url(#plannedGradientCompact)" : "url(#actualGradientCompact)"} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Summary Stats */}
      <Box sx={{ 
        display: 'flex', 
        gap: 3, 
        mb: 3,
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
            {formatCurrencyNumber(totalAmount)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Actual Amount
          </Typography>
        </Box>
        
        {plannedAmount > 0 && (
          <>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.grey[600] }}>
                {formatCurrencyNumber(plannedAmount)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {plannedLabel} Amount
              </Typography>
            </Box>
            
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700, 
                  color: theme.palette[varianceColor].main
                }}
              >
                {formatCurrencyNumber(Math.abs(variance))}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {varianceLabel}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {/* Status Chip */}
      {plannedAmount > 0 ? (
        <Chip
          label={
            isOverPlanned
              ? `${((totalAmount / plannedAmount - 1) * 100).toFixed(1)}% ${isExpense ? 'over budget' : 'above target'}`
              : `${((1 - totalAmount / plannedAmount) * 100).toFixed(1)}% ${isExpense ? 'under budget' : 'below target'}`
          }
          color={varianceColor}
          sx={{ mb: 3, fontWeight: 600 }}
        />
      ) : (
        <Chip
          label={`${noDataLabel} for this period`}
          variant="outlined"
          sx={{ mb: 3 }}
        />
      )}

      {/* Chart */}
      <Box sx={{ width: '100%', maxWidth: 600, height: 250 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={plannedVsActual}
            margin={{ top: 20, right: 40, left: 20, bottom: 20 }}
            barCategoryGap="20%"
          >
            <defs>
              <linearGradient id="plannedGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={theme.palette.grey[400]} stopOpacity={0.8} />
                <stop offset="100%" stopColor={theme.palette.grey[600]} stopOpacity={0.8} />
              </linearGradient>
              <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={theme.palette.primary.light} stopOpacity={0.9} />
                <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0.9} />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="label"
              tick={{ fontSize: 14, fontWeight: 500 }}
              axisLine={false}
              tickLine={false}
            />

            <YAxis
              tickFormatter={(val) => formatCompactCurrencyNumber(val)}
              tick={{ fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />

            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.grey[200]} />

            <Tooltip content={<CustomTooltip />} />

            <Bar
              dataKey="value"
              radius={[8, 8, 0, 0]}
              maxBarSize={120}
            >
              {plannedVsActual.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={index === 0 ? "url(#plannedGradient)" : "url(#actualGradient)"} 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Additional Info */}
      {plannedAmount === 0 && (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ mt: 2, textAlign: 'center', fontStyle: 'italic' }}
        >
          {noDataDescription}
        </Typography>
      )}
    </Box>
  );
};

export default MonthlyReportsActualVsPlannedComponent;
